import { WizErrorManager } from '../../services/core/errors/wiz-error.manager';
import { IWizValidatorDirective } from '../../interfaces/iwiz-error';
import { WizValidationErrors, WizValidatorFn } from '../../services/validators/wiz-validators';
import { Directive, AfterContentInit, ContentChildren, QueryList, ViewContainerRef, OnDestroy, ElementRef, Input, ContentChild } from '@angular/core';
import { WizValidatorDirective } from './wiz-validator.directive';
import { printErrors, wizValidationErrorsEquals } from '../../utils/wiz-validation.utils';
import { WizValidationErrorContainerDirective } from './wiz-validation-error-container.directive';

/**
 * Declares a validation container.
 *
 * A validation container can be any element that holds some WizBaseComponents
 *
 * A validation container can be validated by a call on the checkMethod at any time
 */
@Directive({
  selector: '[wizValidationContainer]'
})
export class WizValidationContainerDirective implements IWizValidatorDirective, AfterContentInit, OnDestroy {
  /**
   * Stores the last resulting errors
   */
  private _lastErrors: WizValidationErrors;

  /**
   * Known component inside this container
   */
  @ContentChildren(WizValidatorDirective, {descendants: false}) validationElements: QueryList<WizValidatorDirective>;
  /**
   * Known sub containers inside this container
   */
  @ContentChildren(WizValidationContainerDirective, {descendants: false}) subContainerElements: QueryList<WizValidationContainerDirective>;

  /**
   * Boolean that indicates the default behavior
   */
  @Input() public wizShowErrors: boolean = WizErrorManager.DEFAULT_SHOW_ERRORS;

  /**
   * Sets an error container (div, hgroup, vgroup) for the errors displayed at a container level
   */
  @ContentChildren(WizValidationErrorContainerDirective, { read: ViewContainerRef, descendants: false }) public errorContainer: QueryList<ViewContainerRef>;

  /**
   * Eventually an additional validation function can be passed on the directive
   */
  @Input() public wizValidationContainer: WizValidatorFn ;

  constructor(private elementRef: ElementRef) { }

  /**
   * Executes the validators for each component in this container
   *
   * It will execute both the given validator and the validators provided by the component if they are any.
   *
   * @param pShowErrors Indicates if must be shown in the container
   * @param pDeep Deep checks. Container will check sub containers recursively if they are any
   * @param pErrorContainer Shows all the errors in the container given rather than the component error container
   * @param pNumResults Number of results to show in the container. Default -1 to show all results
   * @param pClear Indicates if we must clear the container before showing results
   */
  public check( pShowErrors?: boolean,
                pDeep: boolean = false,
                pErrorContainer: ViewContainerRef = null,
                pNumErrors: number = -1,
                pClear: boolean = true): WizValidationErrors {
    // Resulting errors
    let result: WizValidationErrors = [];

    // Which error container to use. We prioritize the one given in parameter
    const errContainer: ViewContainerRef = pErrorContainer ? pErrorContainer : (this.errorContainer?.length > 0 ? this.errorContainer.first : undefined);

    // Error container provided and clear requested
    if (errContainer && pClear) {
      pClear = false;
      errContainer.clear();
    }

    const showErrors = pShowErrors !== undefined ? pShowErrors : this.wizShowErrors;

    // we've got a validator function for the container, we call it
    if (this.wizValidationContainer) {
      result = result.concat(this.wizValidationContainer());

      // Same error as before, we stop right here to avoid flickering
      if (showErrors && errContainer && (errContainer.length <= 0 || !wizValidationErrorsEquals(this._lastErrors, result))) {
        printErrors(result, errContainer, pNumErrors, pClear);
        // set pClear to false, we don't wanna have it cleared by sub elements
        pClear = false;
      }
    }

    // Validates every sub elements if we've got some
    if (this.validationElements && this.validationElements.length > 0) {
      this.validationElements.forEach((element: WizValidatorDirective) => {
        const elementError: WizValidationErrors = element.check(showErrors, pDeep, errContainer, pNumErrors, pClear);

        if (elementError) {
          result = result.concat(elementError);
        }
      });
    }

    // Deep checking requested by the user. We validate every sub container
    if (pDeep && this.subContainerElements && this.subContainerElements.length > 0) {
      this.subContainerElements.forEach((element: WizValidationContainerDirective) => {
        const elementError: WizValidationErrors = element.check(showErrors, pDeep, errContainer, pNumErrors, pClear);

        if (elementError) {
          result = result.concat(elementError);
        }
      });
    }

    return result;
  }

  ngAfterContentInit(): void {
    WizErrorManager.instance.registerValidationContext(this.elementRef, this);
  }
  ngOnDestroy(): void {
    WizErrorManager.instance.unregisterValidationContext(this.elementRef);
  }
}
